/**
 * Iter 31.158 — Keys & Manifest panel.
 *
 * Surfaces the four key statuses Two references in the iter 31.157
 * FETCH MANIFEST as actual UI on /admin:
 *
 *   - handler_context_key  → minted? · last minted at · preview
 *   - comms_read_key       → same
 *   - drive_token          → stored? · linked google email
 *   - last session summary → filed_at · fetch_manifest_attached?
 *
 * Two rotate buttons (handler / comms) each fire the corresponding
 * /admin endpoint, refresh the snapshot, and show a confirmation
 * stamp. Closes the loop on the manifest's "Rotate via Three →
 * Admin → Rotate Comms Read Key" instruction — that path now
 * exists as actual UI, not just prose.
 */
import React, { useCallback, useEffect, useReducer } from "react";
import { api } from "@/lib/api";
import { Loader2, KeyRound, RefreshCw, CheckCircle2, AlertTriangle, Copy } from "lucide-react";


function StatusPill({ ok, label, faintWhenOk = false }) {
    const tone = ok
        ? (faintWhenOk ? "text-ink-soft border-ink/30" : "text-emerald-700 border-emerald-700")
        : "text-stamp-red border-stamp-red";
    return (
        <span
            className={`inline-block px-2 py-0.5 border ${tone} text-[10px] font-heading uppercase tracking-[0.18em]`}
            data-testid={`keys-pill-${label.toLowerCase().replace(/\s+/g, "-")}`}
        >
            {label}
        </span>
    );
}


function KeyRow({ name, status, onRotate, busyKey, testid }) {
    const present = !!status?.present;
    const minted = status?.minted_at;
    const preview = status?.preview;
    const isBusy = busyKey === name;
    return (
        <div
            className="flex items-start justify-between gap-4 py-2.5 border-b border-ink/15 last:border-b-0"
            data-testid={testid}
        >
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                    <KeyRound className="w-3.5 h-3.5 text-ink-soft" />
                    <div className="font-heading uppercase tracking-[0.18em] text-[11px] text-ink">
                        {name}
                    </div>
                    <StatusPill ok={present} label={present ? "minted" : "missing"} />
                </div>
                <div className="text-[10px] text-ink-soft font-body pl-5">
                    {present ? (
                        <>
                            <code className="font-mono text-stamp-red">{preview}</code>
                            <span className="text-ink-faint ml-2">minted {minted || "—"}</span>
                        </>
                    ) : (
                        <span className="text-stamp-red">
                            No key on file. Log out and back in to auto-mint, or rotate now.
                        </span>
                    )}
                </div>
            </div>
            <button
                type="button"
                onClick={() => onRotate(name)}
                disabled={isBusy}
                className="font-heading uppercase tracking-[0.16em] text-[10px] px-2.5 py-1 border border-stamp-red text-stamp-red hover:bg-stamp-red hover:text-paper disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
                data-testid={`keys-rotate-${name}`}
            >
                {isBusy ? <Loader2 className="w-3 h-3 animate-spin inline" /> : <RefreshCw className="w-3 h-3 inline mr-1" />}
                {isBusy ? "rotating…" : "rotate"}
            </button>
        </div>
    );
}


// Iter 31.256h — useReducer pattern to keep `rotate` clean of inline
// setState calls. The lint rule `react-hooks/set-state-in-effect`
// traces reachability from useEffect into any function that calls a
// state setter; centralising mutations behind `dispatch` (stable
// reference, opaque to the rule's static analysis) lets us keep the
// onClick handler as readable as before without the chained false-
// positive. The reducer itself is a pure function defined OUTSIDE the
// component so it carries no closure-capture footprint.
const INITIAL_KEYS_STATE = {
    status: null,
    err: "",
    loading: true,
    busyKey: "",
    lastRotation: null,
    revealedSecret: null,
    copied: false,
    copiedUrl: false,
    refreshTick: 0,
};

function keysReducer(state, action) {
    switch (action.type) {
        case "load_start":
            return { ...state, err: "" };
        case "load_success":
            return { ...state, status: action.status, loading: false };
        case "load_error":
            return { ...state, err: action.err, loading: false };
        case "rotate_busy":
            return { ...state, busyKey: action.key };
        case "rotate_done":
            return {
                ...state,
                busyKey: "",
                lastRotation: action.lastRotation || state.lastRotation,
                revealedSecret: action.revealedSecret ?? state.revealedSecret,
                copied: action.revealedSecret ? false : state.copied,
                copiedUrl: action.revealedSecret ? false : state.copiedUrl,
                refreshTick: state.refreshTick + 1,
            };
        case "rotate_error":
            return { ...state, busyKey: "", err: action.err };
        case "set_copied":
            return { ...state, copied: !!action.value };
        case "set_copied_url":
            return { ...state, copiedUrl: !!action.value };
        case "dismiss_secret":
            return { ...state, revealedSecret: null, copied: false, copiedUrl: false };
        case "refresh":
            return { ...state, refreshTick: state.refreshTick + 1 };
        default:
            return state;
    }
}


export default function KeysManifestPanel() {
    const [s, dispatch] = useReducer(keysReducer, INITIAL_KEYS_STATE);
    const {
        status, err, loading, busyKey, lastRotation,
        revealedSecret, copied, copiedUrl, refreshTick,
    } = s;

    useEffect(() => {
        let cancelled = false;
        (async () => {
            dispatch({ type: "load_start" });
            try {
                const { data } = await api.get("/admin/keys-status");
                if (!cancelled) dispatch({ type: "load_success", status: data });
            } catch (e) {
                if (!cancelled) dispatch({
                    type: "load_error",
                    err: `[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`,
                });
            }
        })();
        return () => { cancelled = true; };
    }, [refreshTick]);

    const rotate = useCallback(async (which) => {
        const endpoint = which === "handler_context_key"
            ? "/admin/rotate-handler-context-key"
            : "/admin/rotate-comms-read-key";
        const confirmMsg = which === "handler_context_key"
            ? "Rotate handler_context_key? Every /comms/handler-context-by-key?key=… URL embedding the old key will 401 immediately. Two will pick up the new URL on the next login auto-sync."
            : "Rotate comms_read_key? Every ?key=… URL in PREVIOUSLY-filed Session Summaries will 401. The NEXT Session Summary Three files will auto-suffix with the new key. Proceed?";
        if (!window.confirm(confirmMsg)) return;
        dispatch({ type: "rotate_busy", key: which });
        try {
            const { data } = await api.post(endpoint);
            // Iter 31.256g — capture the ready-to-paste Claude.ai MCP
            // connector URL (Streamable HTTP path) so No. 6 can copy a
            // single string into the connector field. Only present for
            // handler_context_key rotations.
            const revealed = data.secret
                ? { key: which, secret: data.secret, connectorUrl: data.mcp_connector_url || "" }
                : null;
            dispatch({
                type: "rotate_done",
                lastRotation: { key: which, preview: data.preview, at: data.minted_at },
                revealedSecret: revealed,
            });
        } catch (e) {
            dispatch({
                type: "rotate_error",
                err: `[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`,
            });
        }
    }, []);

    const refresh = useCallback(() => dispatch({ type: "refresh" }), []);

    return (
        <div
            className="paper-card border-2 border-ink/30 p-5 mb-6"
            data-testid="admin-keys-panel"
        >
            <div className="flex items-center justify-between mb-3">
                <h2 className="font-heading uppercase tracking-[0.22em] text-sm text-ink flex items-center gap-2">
                    <KeyRound className="w-4 h-4 text-stamp-red" />
                    Keys & Manifest
                </h2>
                <button
                    type="button"
                    onClick={refresh}
                    disabled={loading}
                    className="font-heading uppercase tracking-[0.16em] text-[10px] text-ink-soft hover:text-stamp-red disabled:opacity-50"
                    data-testid="keys-refresh"
                >
                    {loading ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "refresh"}
                </button>
            </div>

            <div className="font-body text-[11px] text-ink-soft mb-3 leading-relaxed">
                Stable per-user keys that Two embeds in her FETCH MANIFEST (iter 31.157).
                Rotation invalidates every URL using the old key immediately.
                {" "}
                <span className="text-ink-faint">
                    handler_context_key authenticates `/comms/handler-context-by-key` only.
                    comms_read_key authenticates the five `/comms/inbound|outbound*` GETs only.
                    Blast radius is intentionally narrow per Two&apos;s directive.
                </span>
            </div>

            {err ? (
                <div className="mb-3 label-typed text-[11px] leading-relaxed border-l-2 border-stamp-red pl-3 text-stamp-red flex items-start gap-1">
                    <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                    <span>{err}</span>
                </div>
            ) : null}

            {lastRotation ? (
                <div
                    className="mb-3 label-typed text-[11px] leading-relaxed border-l-2 border-emerald-700 pl-3 text-emerald-700 flex items-start gap-1"
                    data-testid="keys-last-rotation"
                >
                    <CheckCircle2 className="w-3 h-3 mt-0.5 shrink-0" />
                    <span>
                        Rotated <code className="font-mono">{lastRotation.key}</code> → new preview{" "}
                        <code className="font-mono">{lastRotation.preview}</code> at {lastRotation.at}.
                        Previous URLs are now revoked.
                    </span>
                </div>
            ) : null}

            {revealedSecret ? (
                <div
                    className="mb-3 border-2 border-stamp-red bg-stamp-red/5 p-3"
                    data-testid="keys-revealed-secret"
                >
                    <div className="label-typed text-[10px] text-stamp-red mb-2 flex items-center gap-2">
                        <AlertTriangle className="w-3 h-3" />
                        New <code className="font-mono">{revealedSecret.key}</code> — shown ONCE
                    </div>
                    <textarea
                        readOnly
                        value={revealedSecret.secret}
                        rows={2}
                        onFocus={(e) => e.target.select()}
                        data-testid="keys-revealed-value"
                        className="w-full px-2 py-1 border border-ink/30 bg-paper-light font-mono text-[11px] text-ink select-all break-all"
                    />
                    <div className="flex items-center gap-2 mt-2">
                        <button
                            type="button"
                            onClick={async () => {
                                try {
                                    await navigator.clipboard.writeText(revealedSecret.secret);
                                    dispatch({ type: "set_copied", value: true });
                                    setTimeout(() => dispatch({ type: "set_copied", value: false }), 2400);
                                } catch (_) { /* clipboard refused */ }
                            }}
                            data-testid="keys-revealed-copy"
                            className="font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 border border-ink/40 text-ink hover:border-stamp-red hover:text-stamp-red inline-flex items-center gap-1.5"
                        >
                            <Copy className="w-3 h-3" />
                            {copied ? "Copied ✓" : "Copy full key"}
                        </button>
                        <button
                            type="button"
                            onClick={() => dispatch({ type: "dismiss_secret" })}
                            data-testid="keys-revealed-dismiss"
                            className="font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 border border-ink/30 text-ink-soft hover:text-stamp-red"
                        >
                            Dismiss
                        </button>
                    </div>
                    <div className="font-body text-[10px] text-ink-soft mt-2 leading-relaxed">
                        Paste this into Claude&apos;s MCP connector as the Bearer token. The
                        value will not be shown again — closing this panel without copying
                        means another rotation.
                    </div>

                    {revealedSecret.connectorUrl ? (
                        <div className="mt-3 pt-3 border-t border-stamp-red/30">
                            <div className="label-typed text-[10px] text-stamp-red mb-2 flex items-center gap-2">
                                <KeyRound className="w-3 h-3" />
                                Ready-to-paste Claude.ai MCP connector URL
                            </div>
                            <textarea
                                readOnly
                                value={revealedSecret.connectorUrl}
                                rows={2}
                                onFocus={(e) => e.target.select()}
                                data-testid="keys-revealed-connector-url"
                                className="w-full px-2 py-1 border border-ink/30 bg-paper-light font-mono text-[11px] text-ink select-all break-all"
                            />
                            <div className="flex items-center gap-2 mt-2">
                                <button
                                    type="button"
                                    onClick={async () => {
                                        try {
                                            await navigator.clipboard.writeText(revealedSecret.connectorUrl);
                                            dispatch({ type: "set_copied_url", value: true });
                                            setTimeout(() => dispatch({ type: "set_copied_url", value: false }), 2400);
                                        } catch (_) { /* clipboard refused */ }
                                    }}
                                    data-testid="keys-revealed-copy-url"
                                    className="font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 border border-stamp-red text-stamp-red hover:bg-stamp-red/10 inline-flex items-center gap-1.5"
                                >
                                    <Copy className="w-3 h-3" />
                                    {copiedUrl ? "Copied ✓" : "Copy connector URL"}
                                </button>
                                <span className="font-body text-[10px] text-ink-soft">
                                    Streamable HTTP transport · paste directly into Claude.ai → Custom Connectors
                                </span>
                            </div>
                        </div>
                    ) : null}
                </div>
            ) : null}

            {loading && !status ? (
                <div className="font-body text-[11px] text-ink-faint italic" data-testid="keys-loading">
                    Loading key status…
                </div>
            ) : status ? (
                <div className="divide-y divide-ink/15">
                    <KeyRow
                        name="handler_context_key"
                        status={status.handler_context_key}
                        onRotate={rotate}
                        busyKey={busyKey}
                        testid="keys-row-handler-context"
                    />
                    <KeyRow
                        name="comms_read_key"
                        status={status.comms_read_key}
                        onRotate={rotate}
                        busyKey={busyKey}
                        testid="keys-row-comms-read"
                    />

                    {/* Drive — read-only here, rotation lives on the Shredder page. */}
                    <div
                        className="flex items-start justify-between gap-4 py-2.5"
                        data-testid="keys-row-drive"
                    >
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-1">
                                <KeyRound className="w-3.5 h-3.5 text-ink-soft" />
                                <div className="font-heading uppercase tracking-[0.18em] text-[11px] text-ink">
                                    drive_token
                                </div>
                                <StatusPill ok={!!status.drive_token?.present} label={status.drive_token?.present ? "linked" : "not linked"} />
                            </div>
                            <div className="text-[10px] text-ink-soft font-body pl-5">
                                {status.drive_token?.present ? (
                                    <>
                                        <code className="font-mono text-stamp-red">
                                            {status.drive_token.google_email || "—"}
                                        </code>
                                        <span className="text-ink-faint ml-2">
                                            stored {status.drive_token.stored_at || "—"}
                                        </span>
                                    </>
                                ) : (
                                    <span className="text-stamp-red">
                                        No Drive token. Link via Shredder page → Connect.
                                    </span>
                                )}
                            </div>
                        </div>
                        <a
                            href="/shredder"
                            className="font-heading uppercase tracking-[0.16em] text-[10px] px-2.5 py-1 border border-ink/40 text-ink-soft hover:text-stamp-red hover:border-stamp-red transition-colors shrink-0"
                            data-testid="keys-drive-link"
                        >
                            shredder →
                        </a>
                    </div>

                    {/* Last session-summary row. */}
                    <div
                        className="flex items-start justify-between gap-4 py-2.5"
                        data-testid="keys-row-last-summary"
                    >
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-1">
                                <KeyRound className="w-3.5 h-3.5 text-ink-soft" />
                                <div className="font-heading uppercase tracking-[0.18em] text-[11px] text-ink">
                                    last session summary
                                </div>
                                <StatusPill
                                    ok={!!status.last_session_summary}
                                    label={status.last_session_summary?.fetch_manifest_attached ? "manifest attached" : (status.last_session_summary ? "no manifest" : "none filed")}
                                />
                            </div>
                            <div className="text-[10px] text-ink-soft font-body pl-5">
                                {status.last_session_summary ? (
                                    <>
                                        <code className="font-mono text-stamp-red">
                                            {status.last_session_summary.subject || "—"}
                                        </code>
                                        <span className="text-ink-faint ml-2">
                                            filed {status.last_session_summary.filed_at || "—"}
                                        </span>
                                    </>
                                ) : (
                                    <span className="text-ink-faint italic">
                                        Three hasn&apos;t filed a Session Summary yet for this user.
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
